import { useEffect, useState } from "react";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { httpService } from "../../services/httpService";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FaIndianRupeeSign } from "react-icons/fa6";
import DynamicTable from "../../components/common/DynamicTable";
import { TbCopyPlusFilled } from "react-icons/tb";
import VetClinicDrawer from "./vetClinicDrawer"; // <-- new import (same folder)
import ToggleSwitch from "../../components/common/ToggleSwitch";
import { useProfile } from "../../context/ProfileContext";
import { useNavigate , useLocation} from "react-router-dom";
import { markSetupStepCompleteById } from "../../utils/vendorSetupSteps";

const MySwal = withReactContent(Swal);

const VetClinicServices = ({ fromSetup: propFromSetup = false }) => {
  const [getAllservice, setGetAllservice] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isServiceDrawerOpen, setIsServiceDrawerOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const { isPlanExpired, vendorId } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();


  // useEffect(() => {
  //   if (location.state?.openAddDrawer) {
  //     setSelectedServiceId(null); 
  //     setIsServiceDrawerOpen(true);
  //     window.history.replaceState({}, document.title);
  //   }
  // }, [location.state]);

  const fromSetup =
    propFromSetup || location.state?.fromGettingStarted === true;

  const handleSaveComplete = async () => {
    if (fromSetup) {
      if (vendorId) {
        await markSetupStepCompleteById(vendorId, "service");
      }
      navigate(location.state?.returnTo || "/admin/getting-started", {
        state: { stepCompleted: "service" },
      });
    }
  };

  const isUnconfigured = (row) => row?.is_configured !== true;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await handleGetAllService();
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isServiceDrawerOpen) handleGetAllService();
  }, [isServiceDrawerOpen]);

  const handleGetAllService = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/getvendorvetclinicservices`,
      );
      if (response.code === 200) setGetAllservice(response?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const getServiceDisplayName = (row) => {
    return (row?.name || "-").trim();
  };

  const getDiscountName = (row) => {
    const discountName =
      row?.applied_discount?.name ||
      row?.discount_name ||
      row?.discount?.name ||
      row?.discountName ||
      "";

    return String(discountName || "").trim();
  };

  const getDiscountTooltipText = (row) => {
    const discountName = getDiscountName(row);
    if (!discountName) return "";
    return `Discount applied for this service: ${discountName}`;
  };

  const deleteService = async (id) => {
    try {
      const response = await httpService.delete(
        `/vendorvetclinicservice/deletevendorvetclinicservice/${id}`,
      );
      if (response.status) {
        toast.success("Service deleted successfully", {
          position: "top-center",
        });
        handleGetAllService();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusToggle = (service) => {
    if (isUnconfigured(service)) {
      toast.info("Please configure this service before changing status.");
      return;
    }
    const newStatus = service.status === "active" ? "inactive" : "active";
    const statusMessage = `Are you sure you want to change the status to ${newStatus}?`;

    MySwal.fire({
      text: statusMessage,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        updateServiceStatus(service._id, newStatus);
      }
    });
  };

  const updateServiceStatus = async (id, status) => {
    try {
      const payload = {
        serviceId: id,
        status: status,
      };
      const res = await httpService.post(
        `/vendorvetclinicservice/change-status`,
        payload,
      );
      if (res?.status) {
        toast.success("Service status updated successfully!");
        handleGetAllService();
      } else {
        console.error("Unexpected response:", res);
      }
    } catch (err) {
      console.error("Error updating service status:", err);
      toast.error(
        err.response?.data?.message || "Failed to update service status.",
      );
    }
  };

  const handleBookableToggle = (service) => {
    if (isUnconfigured(service)) {
      toast.info(
        "Please configure this service before changing bookable status.",
      );
      return;
    }
    const newBookableStatus = service.bookable_online === "yes" ? "no" : "yes";
    if (newBookableStatus === "yes" && service.status !== "active") {
      toast.error("Service must be active before enabling bookable online.");
      return;
    }
    const statusMessage = `Are you sure you want to change the bookable status?`;

    MySwal.fire({
      text: statusMessage,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Calls the new function to make the API request
        updateBookableStatus(service._id, newBookableStatus);
      }
    });
  };

  // New function that sends the API request to a separate endpoint
  const updateBookableStatus = async (id, bookable_online) => {
    try {
      const res = await httpService.post(
        `/vendorvetclinicservice/change-bookable-status`,
        { service_id: id, bookable_online },
      );
      if (res?.status) {
        toast.success("Bookable status updated successfully!");
        handleGetAllService();
      } else {
        console.error("Unexpected response:", res);
      }
    } catch (err) {
      console.error("Error updating bookable status:", err);
      toast.error(
        err.response?.data?.message || "Failed to update bookable status.",
      );
    }
  };

  const handleDeleteService = (id) => {
    MySwal.fire({
      text: "Are you sure you want to delete this service? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteService(id);
        MySwal.fire({
          title: "Deleted!",
          text: "Your Service has been deleted.",
          icon: "success",
        });
      }
    });
  };

  const columns = [
    {
      key: "name",
      header: "Service Name",
      sortable: true,
      renderCell: (row) => {
        const discountName = getDiscountName(row);
        const displayName = getServiceDisplayName(row);
        const tooltipText = getDiscountTooltipText(row);

        return (
          <div className="py-2" style={{ minWidth: "250px", maxWidth: "350px" }}>
            <div className="flex items-center gap-2 flex-wrap">
              <p
                className="text-sm font-medium text-gray-900"
                style={{
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  lineHeight: "1.5",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={displayName}
              >
                {displayName}
              </p>

              {discountName && (
                <span
                  className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-600 shadow-sm cursor-pointer"
                  title={tooltipText}
                >
                  {discountName}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "price",
      header: "Price",
      sortable: true,
      renderCell: (row) => {
        if (
          isUnconfigured(row) ||
          row.basic_price == null ||
          row.basic_price === ""
        ) {
          return <span>-</span>;
        }
        return (
          <span className="items-center flex">
            <FaIndianRupeeSign className="h-3 w-3" />
            {row.basic_price}
          </span>
        );
      },
    },
    {
      key: "pet_type",
      header: "Pet Type",
      sortable: true,
      renderCell: (row) => {
        if (
          isUnconfigured(row) ||
          !Array.isArray(row.pet_type) ||
          row.pet_type.length === 0
        ) {
          return "-";
        }

        const capitalizeFirst = (text = "") =>
          text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

        const petTypes = row.pet_type
          .map((p) => capitalizeFirst(p?.name))
          .filter(Boolean)
          .join(", ");

        const maxDisplayLength = 30; // Show first 30 characters
        const shouldTruncate = petTypes.length > maxDisplayLength;
        const displayText = shouldTruncate
          ? petTypes.substring(0, maxDisplayLength) + "..."
          : petTypes;

        return (
          <div className="py-2" style={{ maxWidth: "200px" }}>
            <p
              className="text-sm font-medium text-gray-900"
              style={{
                wordBreak: "break-word",
                whiteSpace: "normal",
                lineHeight: "1.5",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={petTypes} // Shows full list on hover
            >
              {displayText}
            </p>
          </div>
        );
      },
    },
    {
      key: "duration",
      header: "Duration",
      sortable: true,
      sortValue: (row) => {
        // Helper to get total minutes from duration
        const getTotalMinutes = (row) => {
          // Check if duration is in flat format (hour/minute objects)
          const isFlat = row?.duration?.duration_based_on === "flat";
          if (isFlat) {
            const hours = Number(row.duration?.flat?.hour) || 0;
            const minutes = Number(row.duration?.flat?.minutes) || 0;
            return hours * 60 + minutes;
          }

          // Check if duration is directly in minutes
          if (row?.duration_minutes) {
            return row.duration_minutes;
          }

          return 0; // Default value for sorting
        };

        return getTotalMinutes(row);
      },
      renderCell: (row) => {
        if (isUnconfigured(row)) {
          return <span className="items-center">-</span>;
        }

        // Helper to format duration in hours and minutes
        const formatDuration = (totalMinutes) => {
          if (!totalMinutes || totalMinutes === 0) return "-";

          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;

          if (hours > 0 && minutes > 0) {
            return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} min${minutes !== 1 ? "s" : ""}`;
          } else if (hours > 0) {
            return `${hours} hour${hours !== 1 ? "s" : ""}`;
          } else {
            return `${minutes} min${minutes !== 1 ? "s" : ""}`;
          }
        };

        // Check if duration is in flat format (hour/minute objects)
        const isFlat = row?.duration?.duration_based_on === "flat";
        if (isFlat) {
          const hours = Number(row.duration?.flat?.hour) || 0;
          const minutes = Number(row.duration?.flat?.minutes) || 0;
          const totalMinutes = hours * 60 + minutes;
          return (
            <span className="items-center">{formatDuration(totalMinutes)}</span>
          );
        }

        // Check if duration is directly in minutes
        if (row?.duration_minutes) {
          return (
            <span className="items-center">
              {formatDuration(row.duration_minutes)}
            </span>
          );
        }

        return <span className="items-center">-</span>;
      },
    },
    {
      key: "barcode",
      header: "Barcode",
      sortable: true,
      renderCell: (row) => (
        <div className="py-2">
          <span
            className="text-sm"
            title={row.barcode}
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "150px",
            }}
          >
            {isUnconfigured(row) || !row.barcode ? "-" : row.barcode}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: false,
      renderCell: (row) => (
        <ToggleSwitch
          isActive={row.status === "active"}
          onChange={() => handleStatusToggle(row)}
          disabled={isUnconfigured(row)}
        />
      ),
    },
    {
      key: "bookable",
      header: "Bookable Online",
      sortable: false,
      renderCell: (row) => (
        <ToggleSwitch
          isActive={row.bookable_online === "yes"}
          onChange={() => handleBookableToggle(row)}
          disabled={isUnconfigured(row)}
        />
      ),
    },
  ];

  const handleEdit = (service) => {
    setSelectedServiceId(service._id);
    setIsServiceDrawerOpen(true);
  };

  const handleClone = (service) => {
    MySwal.fire({
      text: "Are you sure you want to make a copy of this service?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await httpService.post(
            "/vendorvetclinicservice/copyvendorvetclinicservice",
            { serviceId: service._id },
          );
          if (res?.status) {
            toast.success("Copy Created Successfully!!");
            handleGetAllService();
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to clone service");
        }
      }
    });
  };

  const actions = [
    {
      label: "Make a Copy",
      icon: (
        <TbCopyPlusFilled className="w-5 h-5 text-gray-600 group-hover:text-yellow-600 transition-colors" />
      ),
      onClick: (row) => handleClone(row),
      colorClasses: "hover:border-yellow-500 hover:bg-yellow-50",
      shouldShow: (row) => {
        return (
          !isPlanExpired && row?.is_configured === true && !row?.vet_service_id
        );
      },
    },
    {
      label: "Edit",
      icon: (
        <PencilIcon className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
      ),
      onClick: (row) => handleEdit(row),
      colorClasses: "hover:border-green-500 hover:bg-green-50",
      shouldShow: (row) => {
        return !isPlanExpired;
      },
    },
    {
      label: "Delete",
      icon: (
        <TrashIcon className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" />
      ),
      onClick: (row) => handleDeleteService(row._id),
      colorClasses: "hover:border-red-500 hover:bg-red-50",
      shouldShow: (row) => !row?.vet_service_id,
    },
  ];

  return (
    <div>
      {loading && <LoaderSpiner text="Loading . . ." />}
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="text-2xl font-semibold">Vet Clinic Services</h1>
          <p className="text-sm text-gray-500 w-full">
            Services are managed from the platform catalog. Use Edit to set
            price, duration, and pet types.
          </p>
        </div>
      </div>

      <DynamicTable
        columns={columns}
        data={getAllservice}
        actions={actions}
        showPagination={true}
        initialPageSize={10}
        showPageInfo={true}
        searchable={true}
        searchPlaceholder="Search vet clinic services..."
      />

      <VetClinicDrawer
        isOpen={isServiceDrawerOpen}
        serviceId={selectedServiceId}
        onClose={() => {
          setIsServiceDrawerOpen(false);
          setSelectedServiceId(null);
        }}
        fromSetup={fromSetup}
        onSaveComplete={handleSaveComplete}
      />
    </div>
  );
};

export default VetClinicServices;